import { getPersistedValue, removePersistedValue, setPersistedValue } from 'shared/utils/persisted-values';

import { getLogger } from 'src/utils/datadog-logger';

export const PBB_DECLINE_STORAGE_KEY = 'pbbCheckoutDecline';
export const PBB_DECLINED_STATUS = 'STATUS_DECLINED';

type PersistedPbbDecline = {
  checkoutToken: string;
  userId: string;
  reasonCode: string;
};

type PbbDeclineInput = {
  enabled: boolean;
  status?: string;
  checkoutToken?: string | null;
  userId?: string | null;
  reasonCode?: string | null;
};

type PbbDeclineMatch = {
  checkoutToken?: string | null;
  userId?: string | null;
};

const logger = getLogger('pbb-decline-storage');

function isPersistedPbbDecline(value: unknown): value is PersistedPbbDecline {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const record = value as Partial<PersistedPbbDecline>;
  return (
    typeof record.checkoutToken === 'string' &&
    Boolean(record.checkoutToken) &&
    typeof record.userId === 'string' &&
    Boolean(record.userId) &&
    typeof record.reasonCode === 'string' &&
    Boolean(record.reasonCode)
  );
}

export function persistPbbDecline({ enabled, status, checkoutToken, userId, reasonCode }: PbbDeclineInput): void {
  if (!enabled || status !== PBB_DECLINED_STATUS || !checkoutToken || !userId || !reasonCode) {
    return;
  }

  try {
    setPersistedValue(PBB_DECLINE_STORAGE_KEY, { checkoutToken, userId, reasonCode }, true);
  } catch (error) {
    logger.warn('failed to persist PBB checkout decline; failing open', { error });
  }
}

function getMatchingPbbDecline({ checkoutToken, userId }: PbbDeclineMatch): PersistedPbbDecline | null {
  if (!checkoutToken || !userId) {
    return null;
  }

  try {
    const persistedDecline = getPersistedValue(PBB_DECLINE_STORAGE_KEY);
    if (
      !isPersistedPbbDecline(persistedDecline) ||
      persistedDecline.checkoutToken !== checkoutToken ||
      persistedDecline.userId !== userId
    ) {
      return null;
    }

    return persistedDecline;
  } catch (error) {
    logger.warn('failed to read PBB checkout decline; failing open', { error });
    return null;
  }
}

/** Reads without consuming so React can decide what to render without a render-phase side effect. */
export function hasMatchingPbbDecline(match: PbbDeclineMatch): boolean {
  return Boolean(getMatchingPbbDecline(match));
}

/** Consumes after the suppressed render commits and emits rollout telemetry once. */
export function consumeMatchingPbbDecline(match: PbbDeclineMatch): void {
  const persistedDecline = getMatchingPbbDecline(match);
  if (!persistedDecline) {
    return;
  }

  try {
    removePersistedValue(PBB_DECLINE_STORAGE_KEY, true);
    logger.info('suppressed PBB CTA after matching checkout decline', persistedDecline);
  } catch (error) {
    logger.warn('failed to consume PBB checkout decline; failing open', { error });
  }
}
